<script setup lang="ts">
defineProps({
  showPrompt: {
    default: true,
    type: Boolean,
  },
})

const { t } = useI18n()
const container = useTemplateRef('container')
const animationRotating = useTemplateRef('animationRotating')
const isVisible = useElementVisibility(container)

const currentSlotIndex = ref(0)
const totalRowsToDisplay = ref(1)
const typewriterText = ref('')

const completion: InputCompletion = {
  color: 'indigo',
  key: 'enrich',
  label: 'enrich',
}

const columns: SchemaTableColumn[] = [
  {
    icon: getIcon('name'),
    key: 'name',
    size: 2,
    title: t('columns.name'),
  },
  {
    icon: getIcon('industries'),
    key: 'industries',
    size: 2,
    title: t('columns.industries'),
  },
  {
    icon: getIcon('revenue'),
    key: 'revenue',
    size: 1,
    title: t('columns.revenue'),
  },
]

const rows: SchemaTableRow[] = [
  {
    domain: 'thecompaniesapi.com',
    image: '/images/favicons/apple-touch-icon.png',
    industries: [
      'B2B',
      'Data',
      'SaaS',
    ],
    name: 'The Companies API',
    revenue: '1m-10m',
  },
  {
    domain: 'piedpiper.com',
    image: getLogo('piedpiper.com', 'companies')!,
    industries: ['Compression', 'Streaming'],
    name: 'PiedPiper',
    revenue: 'under-1m',
  },
  {
    domain: 'waystar-royco.com',
    image: getLogo('waystar-royco.com', 'companies')!,
    industries: ['News', 'Entertainment'],
    name: 'Waystar Royco',
    revenue: '1b-10b',
  },
  {
    domain: 'wayne-enterprises.com',
    image: getLogo('wayne-enterprises.com', 'companies')!,
    industries: ['Defense', 'Transportation'],
    name: 'Wayne Enterprises',
    revenue: 'over-10b',
  },
  {
    domain: 'umbrella.corp',
    image: getLogo('umbrella.corp', 'companies')!,
    industries: ['Biotech', 'Military'],
    name: 'Umbrella Corporation',
    revenue: 'over-10b',
  },
] as const

const slots = [
  ...rows.filter(row => row.name !== 'The Companies API').reduce(
    (accumulator: string[], row) => {
      accumulator.push(String(row.domain))
      accumulator.push(`${row.domain}-loading`)

      return accumulator
    },
    [],
  ),
  'completed',
]

const isLoading = computed(() => {
  return slots[currentSlotIndex.value].endsWith('-loading')
})

const rowsToDisplay = computed(() => {
  return rows.slice(0, totalRowsToDisplay.value)
})

watch(currentSlotIndex, (value) => {
  typewriterText.value = ''

  if (value % 2 === 1) {
    totalRowsToDisplay.value = Math.min(totalRowsToDisplay.value + 1, rows.length)
  }
})

watch(isVisible, (value) => {
  if (value) {
    currentSlotIndex.value = 0
    totalRowsToDisplay.value = 1
    animationRotating.value?.reset()
  }
})

function getRowFromDomain(domain: string) {
  return rows.find(row => row.domain === domain)
}
</script>

<template>
  <div
    ref="container"
    class="size-full min-h-96 gap-12 lg:flex lg:flex-col lg:items-center lg:justify-center"
  >
    <SchemaTable
      :columns="columns"
      :loadable-columns="['revenue', 'industries']"
      :loading-text="t('globals.enriching')"
      :rows="rowsToDisplay"
    />

    <div
      class="absolute bottom-8 left-0 w-full px-6"
      :class="{ hidden: !showPrompt }"
    >
      <Transition
        enter-active-class="transition-base delay-150"
        enter-from-class="opacity-0 translate-y-24"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-base"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-24"
      >
        <Prompt
          v-if="isVisible"
          class="z-20"
          :completable="false"
          :completion="completion"
          :completions="[completion]"
          :hoverable="false"
          is-preview
          light
          :loading="isLoading"
          :show-cleanup="false"
          :submittable="!!typewriterText.length"
        >
          <template #input>
            <div class="overflow-hidden">
              <AnimationRotating
                ref="animationRotating"
                v-model="currentSlotIndex"
                direction="vertical"
                once
                :slots="slots"
              >
                <template
                  v-for="slot in slots"
                  :key="slot"
                  #[slot]
                >
                  <div class="flex items-center gap-2">
                    <template v-if="slot.endsWith('-loading')">
                      {{ t('enrichingWithDomain', { domain: slot.replace('-loading', '') }) }}
                    </template>

                    <template v-else>
                      <AnimateTypewriter
                        v-model="typewriterText"
                        :speed="100"
                        :text="getRowFromDomain(slot)?.name as string || t('completed')"
                      />
                    </template>
                  </div>
                </template>
              </AnimationRotating>
            </div>
          </template>
        </Prompt>
      </Transition>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "columns": {
      "industries": "Industries",
      "name": "Name",
      "revenue": "Revenue"
    },
    "completed": "Ready to enrich your customers?",
    "enrichingWithDomain": "Enriching {domain}..."
  },
  "es": {
    "columns": {
      "industries": "Industrias",
      "name": "Nombre",
      "revenue": "Ingresos"
    },
    "completed": "¿Listo para enriquecer tus clientes?",
    "enrichingWithDomain": "Enriqueciendo {domain}..."
  },
  "fr": {
    "columns": {
      "industries": "Industries",
      "name": "Nom",
      "revenue": "Revenue"
    },
    "completed": "Prêt à enrichir vos clients ?",
    "enrichingWithDomain": "Enrichissement {domain}..."
  }
}
</i18n>
